/* eslint-disable @typescript-eslint/only-throw-error */
import { PermissionAdminLevel, PermissionLevel } from '@/api/User/types.ts'
import { Languages } from '@/components/LanguageSelector/types.ts'
import { toaster } from '@/components/ui/toaster'
import type { AuthUser } from '@/hooks/useGetCurrentUser/types'
import {
  getSingleInitiativeRedirectParams,
  hasInitiativeAccess,
  shouldShowInitiativesCrumb,
} from '@/hooks/useGetCurrentUser/utils'
import InitiativeViewPending from '@/pages/Initiatives/Home/Pending'
import { getBusinessQueryOptions } from '@/queries/Business'
import {
  fetchCollectAuditsQueryOptions,
  getCollectQueryOptions,
} from '@/queries/Collect'
import {
  getFeatureQueryOptions,
  getUserPreferenceQueryOptions,
} from '@/queries/Feature'
import { getGroupQueryOptions } from '@/queries/Group'
import { getInitiativeQueryOptions } from '@/queries/Initiative'
import { getMeOptions, getUserQueryOptions } from '@/queries/User'
import { getWorkflowTemplateQueryOptions } from '@/queries/WorkflowTemplate'
import type {
  NotAuthorizedSearchSchema,
  PaginatedInitiativeSearchSchema,
  PaginatedOrderedQuerySearchSchema,
  PaginatedQuerySearchSchema,
  PaginatedSearchSchema,
  SelfAppliedCollectTotpSearchSchema,
} from '@/routes/schemas'
import {
  enrichmentUserWithMethods,
  getCurrentUser,
  hasUserLocale,
  saveCurrentUser,
} from '@/utils/auth.util'
import { normalizeBusinessSearch } from '@/utils/businessSearch.util'
import { newLayoutInitiatives } from '@/utils/featureFlags.ts'
import { hasLostInitiativeAccess } from '@/utils/initiativeAccess.util'
import isLanguageSupported from '@/utils/isLanguageSupported.ts'
import { applyLocale, resolveLocale } from '@/utils/locale.ts'
import {
  getNotAuthorizedSearch,
  parseNotAuthorizedSearch,
} from '@/utils/notAuthorized.util'
import queryClient from '@/utils/queryClient.ts'
import { refreshCurrentUser } from '@/utils/refreshCurrentUser.util'
import {
  createRootRouteWithContext,
  createRoute,
  notFound,
  redirect,
} from '@tanstack/react-router'
import { t } from 'i18next'
import { lazy } from 'react'
import { withAuthenticationRequired } from 'react-oidc-context'

const CollectView = lazy(() => import('@/pages/Collects/CreateEdit/View'))
const CollectEdit = lazy(() => import('@/pages/Collects/CreateEdit/Edit'))
const CollectAuditList = lazy(() => import('@/pages/Collects/AuditList'))
const AuditView = lazy(() => import('@/pages/Collects/AuditView'))
const InitiativeDuplicate = lazy(() => import('@/pages/Initiatives/Duplicate'))
const CollectList = lazy(() => import('@/pages/Collects/List'))
const CollectCreate = lazy(() => import('@/pages/Collects/CreateEdit/Create'))
const BusinessCreate = lazy(
  () => import('@/pages/Businesses/CreateEdit/Create')
)
const BusinessEdit = lazy(() => import('@/pages/Businesses/CreateEdit/Edit'))
const BusinessList = lazy(() => import('@/pages/Businesses/List'))
const ConstructorFieldsLayout = lazy(
  () => import('@/pages/Constructor/CreateEdit')
)
const FeatureCreate = lazy(() => import('@/pages/Features/CreateEdit/Create'))
const FeatureEdit = lazy(() => import('@/pages/Features/CreateEdit/Edit'))
const FeatureAnalytics = lazy(() => import('@/pages/Features/Analytics'))
const FeaturesDuplicate = lazy(() => import('@/pages/Features/Duplicate'))
const GroupCreate = lazy(() => import('@/pages/Groups/CreateEdit/Create'))
const GroupEdit = lazy(() => import('@/pages/Groups/CreateEdit/Edit'))
const GroupList = lazy(() => import('@/pages/Groups/List'))
const InitiativeCreate = lazy(
  () => import('@/pages/Initiatives/CreateEdit/Create')
)
const InitiativeEdit = lazy(() => import('@/pages/Initiatives/CreateEdit/Edit'))
const InitiativeList = lazy(() => import('@/pages/Initiatives/List'))
const InitiativeListLegacy = lazy(
  () => import('@/pages/Initiatives/ListLegacy')
)
const InternalCollect = lazy(
  () => import('@/pages/Collects/CreateEdit/Internal')
)
const InitiativeHome = lazy(() => import('@/pages/Initiatives/Home'))
const EmailTemplateCreate = lazy(
  () => import('@/pages/EmailTemplates/CreateEdit/Create')
)
const EmailTemplateEdit = lazy(
  () => import('@/pages/EmailTemplates/CreateEdit/Edit')
)
const NotAuthorized = lazy(() => import('@/pages/NotAuthorized'))
const UserCreate = lazy(() => import('@/pages/Users/CreateEdit/Create'))
const UserEdit = lazy(() => import('@/pages/Users/CreateEdit/Edit'))
const UserList = lazy(() => import('@/pages/Users/List'))
const Layout = lazy(() => import('@/pages/Layout/layout.tsx'))
const ImportDataView = lazy(() => import('@/pages/ImportedData/Create'))
const ExportDataView = lazy(() => import('@/pages/ExportData/Create'))
const InitiativeImports = lazy(() => import('@/pages/Initiatives/Imports'))
const InitiativeExports = lazy(() => import('@/pages/Initiatives/Exports'))
const SelfAppliedCollect = lazy(
  () => import('@/pages/Collects/CreateEdit/SelfApplied')
)
const SelfAppliedCollectFinished = lazy(
  () => import('@/pages/Collects/CreateEdit/SelfApplied/Finished/index.tsx')
)

const SelfAppliedCollectTotp = lazy(
  () => import('@/pages/Collects/CreateEdit/SelfApplied/Totp')
)
const Login = lazy(() => import('@/pages/Login'))
const TermsOfUse = lazy(() => import('@/pages/TermsOfUse'))

interface RedirectOptions {
  locale?: string
  params?: Record<string, string>
  search?: object
  replace?: boolean
}

function redirectTo(to?: string, options?: RedirectOptions) {
  throw redirect({
    to: to,
    params: {
      locale: resolveLocale(options?.locale ?? navigator.language),
      ...options?.params,
    } as unknown as true,
    search: options?.search as unknown as true,
    replace: options?.replace,
  })
}

interface NotAuthorizedRedirectOptions {
  initiativeId?: string
  locale: string
}

function redirectToNotAuthorized({
  initiativeId,
  locale,
}: NotAuthorizedRedirectOptions) {
  redirectTo(NotAuthorizedRoute.to, {
    locale,
    search: getNotAuthorizedSearch(getCurrentUser(), initiativeId),
  })
}

async function ensureInitiative({
  params: { initiativeId },
}: InitiativeRouteContext) {
  try {
    await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
  } catch {
    throw notFound()
  }
}

function requireInitiativeAdmin({
  params: { initiativeId, locale },
}: InitiativeRouteContext) {
  const user = getCurrentUser()
  if (!user?.hasPermission(PermissionLevel.ADMIN, Number(initiativeId))) {
    redirectToNotAuthorized({ initiativeId, locale })
  }
}

export const Route = createRootRouteWithContext<object>()({
  component: Layout,
})

export const LoginRoute = createRoute({
  path: '/login',
  getParentRoute: () => Route,
  component: Login,
  staticData: { noLayout: true },
})

export const TermsOfUseRedirectRoute = createRoute({
  path: 'termos-de-uso',
  getParentRoute: () => Route,
  beforeLoad: () => {
    redirectTo(TermsOfUseRoute.to)
  },
})

export const HomeRoute = createRoute({
  path: '/',
  getParentRoute: () => Route,
  loader: async () => {
    let user
    if (hasUserLocale()) {
      user = getCurrentUser()
    } else {
      const result = await queryClient.ensureQueryData(getMeOptions())
      user = enrichmentUserWithMethods(result)
      saveCurrentUser(result)
    }

    if (
      user?.hasAnyInitiativeOrBelowPermission(PermissionLevel.READ) ||
      user?.hasAnyInitiativeDashboardAccess()
    ) {
      redirectTo(InitiativesRoute.to, { replace: true })
    } else if (
      user?.hasAdminPermission('business', PermissionAdminLevel.READ)
    ) {
      redirectTo(BusinessesRoute.to, { replace: true })
    } else if (user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(UsersRoute.to, { replace: true })
    }
    redirectTo(PrivateRoute.to, { replace: true })
  },
})

export const LocaleRoute = createRoute({
  path: '$locale/',
  getParentRoute: () => Route,
  beforeLoad: ({ params, location, preload }) => {
    const pathname = location.pathname

    if (params.locale === null || !isLanguageSupported(params.locale)) {
      redirectTo(undefined, { replace: true })
    }

    if (!preload) {
      applyLocale(params.locale as Languages)
    }

    const localePattern = new RegExp(`^/${params.locale}/?$`)

    if (pathname.match(localePattern)) {
      redirectTo(InitiativesRoute.to, { locale: params.locale, replace: true })
    }
  },
})

export const PrivateRoute = createRoute({
  id: 'private',
  getParentRoute: () => LocaleRoute,
  beforeLoad: async () => {
    try {
      let currentUser: AuthUser | undefined
      if (hasUserLocale()) {
        currentUser = getCurrentUser()
      } else {
        const result = await queryClient.ensureQueryData(getMeOptions())
        currentUser = enrichmentUserWithMethods(result)
        saveCurrentUser(result)
      }

      return { user: currentUser }
    } catch {
      redirectTo(LoginRoute.to)
    }
  },
})

export const BusinessesRoute = createRoute({
  path: 'negocios',
  getParentRoute: () => PrivateRoute,
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  loader: () => {
    return { crumb: 'Negócios' }
  },
})

export const BusinessesListRoute = createRoute({
  path: '/',
  getParentRoute: () => BusinessesRoute,
  component: withAuthenticationRequired(BusinessList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedQuerySearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 9),
    }
  },
})

export const BusinessesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => BusinessesRoute,
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  component: withAuthenticationRequired(BusinessCreate),
})

export const BusinessesEditRoute = createRoute({
  path: '$businessId/editar',
  getParentRoute: () => BusinessesRoute,
  component: withAuthenticationRequired(BusinessEdit),
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('business', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  loader: async ({ params: { businessId } }) => {
    try {
      await queryClient.ensureQueryData(getBusinessQueryOptions(businessId))
    } catch {
      throw notFound()
    }
  },
})

export const InitiativesRoute = createRoute({
  path: 'iniciativas',
  getParentRoute: () => PrivateRoute,
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasAnyInitiativeOrBelowPermission(PermissionLevel.READ) &&
      !user?.hasAnyInitiativeDashboardAccess()
    ) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  // refresh /me once per entry into the area, not on every child navigation
  shouldReload: false,
  loader: async () => {
    await refreshCurrentUser()
    if (!shouldShowInitiativesCrumb(getCurrentUser())) return
    return { crumb: 'Iniciativas' }
  },
})

export const InitiativesListRoute = createRoute({
  path: '/',
  getParentRoute: () => InitiativesRoute,
  beforeLoad: ({ params: { locale } }) => {
    const params = getSingleInitiativeRedirectParams(getCurrentUser(), locale)

    if (params !== undefined) {
      redirectTo(InitiativesViewRoute.to, { params, replace: true })
    }
  },
  component: withAuthenticationRequired(
    newLayoutInitiatives ? InitiativeList : InitiativeListLegacy
  ),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedInitiativeSearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 12),
      business: normalizeBusinessSearch(search.business),
      order: search.order === 'recent' ? 'recent' : undefined,
    }
  },
})

export const InitiativesEditRoute = createRoute({
  path: '$initiativeId/editar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeEdit),
  beforeLoad: requireInitiativeAdmin,
  loader: ensureInitiative,
})

interface InitiativeRouteContext {
  params: { initiativeId: string; locale: string }
}

async function loadInitiativeDetailFresh({
  params: { initiativeId, locale },
}: InitiativeRouteContext) {
  try {
    // always revalidate: a cached accessLevel may predate a role downgrade
    await queryClient.fetchQuery({
      ...getInitiativeQueryOptions(initiativeId, true),
      staleTime: 0,
    })
  } catch (error) {
    if (await hasLostInitiativeAccess(error, initiativeId)) {
      toaster.create({
        description: t('initiative_access_removed', {
          lng: locale,
          ns: 'common',
        }),
        type: 'info',
      })
      redirectTo(InitiativesListRoute.to, { locale, replace: true })
    }
    throw notFound()
  }
}

function requireInitiativeDashboardAccess({
  params: { initiativeId, locale },
}: InitiativeRouteContext) {
  const user = getCurrentUser()
  if (!user?.hasInitiativeDashboardAccess(Number(initiativeId))) {
    redirectToNotAuthorized({ initiativeId, locale })
  }
}

export const InitiativesViewRoute = createRoute({
  path: '$initiativeId/visualizar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeHome),
  pendingComponent: InitiativeViewPending,
  beforeLoad: requireInitiativeDashboardAccess,
  loader: loadInitiativeDetailFresh,
})

export const InternalCollectRoute = createRoute({
  path: '$initiativeId/coleta-interna/$featureId',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InternalCollect),
  beforeLoad: requireInitiativeDashboardAccess,
  loader: loadInitiativeDetailFresh,
})

export const InitiativesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeCreate),
  beforeLoad: async ({ params: { locale }, search }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('initiative', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
      return
    }

    if ('businessId' in search) {
      try {
        await queryClient.ensureQueryData(
          getBusinessQueryOptions(Number(search.businessId))
        )
      } catch {
        throw notFound()
      }
    }
  },
})

export const EmailTemplatesRoute = createRoute({
  path: '$initiativeId/templates-email',
  getParentRoute: () => InitiativesRoute,
  beforeLoad: ({ params: { initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasPermission(PermissionLevel.ADMIN, Number(initiativeId))) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
})

export const EmailTemplatesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => EmailTemplatesRoute,
  component: withAuthenticationRequired(EmailTemplateCreate),
})

export const EmailTemplatesEditRoute = createRoute({
  path: '$templateId',
  getParentRoute: () => EmailTemplatesRoute,
  component: withAuthenticationRequired(EmailTemplateEdit),
  loader: async ({ params: { initiativeId, templateId } }) => {
    try {
      await queryClient.ensureQueryData(
        getWorkflowTemplateQueryOptions(initiativeId, templateId)
      )
    } catch {
      throw notFound()
    }
  },
})

export const UsersRoute = createRoute({
  path: 'usuarios',
  getParentRoute: () => PrivateRoute,
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  loader: () => {
    return { crumb: 'Usuários' }
  },
})

export const UsersListRoute = createRoute({
  path: '/',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedOrderedQuerySearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
      orderBy: 'createdAt',
      orderSort: 'DESC',
    }
  },
})

export const UsersEditRoute = createRoute({
  path: '$userId/editar',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserEdit),
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  loader: async ({ params: { userId } }) => {
    try {
      await queryClient.ensureQueryData(getUserQueryOptions(userId))
    } catch {
      throw notFound()
    }
  },
})

export const UsersEditPermissionsRoute = createRoute({
  path: '$userId/permissoes',
  getParentRoute: () => UsersRoute,
  loader: async ({ params: { userId } }) => {
    try {
      await queryClient.ensureQueryData(getUserQueryOptions(userId))
    } catch {
      throw notFound()
    }
  },
})

export const UsersCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => UsersRoute,
  component: withAuthenticationRequired(UserCreate),
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
})

export const GroupsRoute = createRoute({
  path: 'grupos',
  getParentRoute: () => PrivateRoute,
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.READ)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
})

export const GroupsListRoute = createRoute({
  path: '/',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupList),
  validateSearch: (
    search: Record<string, unknown>
  ): PaginatedOrderedQuerySearchSchema => {
    return {
      filter: (search.filter as string) || '',
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
      orderBy: 'name',
      orderSort: 'DESC',
    }
  },
})

export const GroupsEditRoute = createRoute({
  path: '$groupId/editar',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupEdit),
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.ADMIN)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
  loader: async ({ params: { groupId } }) => {
    try {
      await queryClient.ensureQueryData(getGroupQueryOptions(groupId))
    } catch {
      throw notFound()
    }
  },
})

export const GroupsCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => GroupsRoute,
  component: withAuthenticationRequired(GroupCreate),
  beforeLoad: ({ params: { locale } }) => {
    const user = getCurrentUser()
    if (!user?.hasAdminPermission('user', PermissionAdminLevel.WRITE)) {
      redirectTo(NotAuthorizedRoute.to, { locale })
    }
  },
})

export const FeaturesRoute = createRoute({
  path: '$initiativeId/funcionalidades',
  getParentRoute: () => InitiativesRoute,
  loader: () => {
    return { crumb: 'Funcionalidades' }
  },
  beforeLoad: ({ params: { initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (!user || !hasInitiativeAccess(user, Number(initiativeId))) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
})

export const FeaturesCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureCreate),
})

export const FeaturesEditRoute = createRoute({
  path: '$featureId',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureEdit),
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
})

export const FeaturesAnalyticsRoute = createRoute({
  path: '$featureId/estatisticas',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeatureAnalytics),
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
})

export const FeaturesDuplicateRoute = createRoute({
  path: '$featureId/duplicar',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(FeaturesDuplicate),
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
  },
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.WRITE,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
})

export const CollectsRoute = createRoute({
  path: '$featureId/coletas',
  getParentRoute: () => FeaturesRoute,
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.LOOKUP,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
})

export const CollectsListRoute = createRoute({
  path: '/',
  getParentRoute: () => CollectsRoute,
  validateSearch: (search: Record<string, unknown>): PaginatedSearchSchema => {
    return {
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
    }
  },
  beforeLoad: async ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    let feature
    try {
      const [feat, _] = await Promise.all([
        queryClient.ensureQueryData(
          getFeatureQueryOptions(initiativeId, featureId)
        ),
        queryClient.ensureQueryData(
          getUserPreferenceQueryOptions(
            initiativeId,
            featureId,
            'collects_selected_projections'
          )
        ),
      ])
      feature = feat
    } catch {
      throw notFound()
    }

    const hasPublishedLayoutAccess = feature.publishedLayouts.some((layout) =>
      user?.hasPermission(
        PermissionLevel.LOOKUP,
        Number(initiativeId),
        Number(featureId),
        layout === 'WEB' ? 'web' : 'mobile'
      )
    )

    if (!hasPublishedLayoutAccess) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
  loader: () => {
    return { crumb: 'Coletas' }
  },
  component: withAuthenticationRequired(CollectList),
})

export const CollectsCreateRoute = createRoute({
  path: 'criar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectCreate),
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.COLLECTOR,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
  loader: async ({ params: { featureId, initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(
        getFeatureQueryOptions(initiativeId, featureId)
      )
    } catch {
      throw notFound()
    }
    return { crumb: 'Coletas' }
  },
})

export const CollectsEditRoute = createRoute({
  path: '$collectId/editar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectEdit),

  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.WRITE,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
  loader: async ({ params: { initiativeId, featureId, collectId } }) => {
    const results = await Promise.allSettled([
      queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'web', collectId)
      ),
      queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'mobile', collectId)
      ),
    ])
    if (results.every((result) => result.status === 'rejected')) {
      throw notFound()
    }
  },
})

export const CollectsViewRoute = createRoute({
  path: '$collectId/visualizar',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectView),

  loader: async ({ params: { initiativeId, featureId, collectId } }) => {
    const results = await Promise.allSettled([
      queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'web', collectId)
      ),
      queryClient.ensureQueryData(
        getCollectQueryOptions(initiativeId, featureId, 'mobile', collectId)
      ),
    ])
    if (results.every((result) => result.status === 'rejected')) {
      throw notFound()
    }
  },
})

export const CollectAuditsListRoute = createRoute({
  path: '$collectId/auditoria',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(CollectAuditList),
  loader: async (loaderContext) => {
    const { initiativeId, featureId, collectId } = loaderContext.params
    const { page, limit } = loaderContext.location.search as {
      page: number
      limit: number
    }
    try {
      await queryClient.ensureQueryData(
        fetchCollectAuditsQueryOptions(initiativeId, featureId, collectId, {
          page,
          limit,
        })
      )
    } catch {
      throw notFound()
    }
  },
  validateSearch: (search: Record<string, unknown>): PaginatedSearchSchema => {
    return {
      page: Number(search?.page ?? 0),
      limit: Number(search?.limit ?? 10),
    }
  },
})

export const CollectAuditsViewRoute = createRoute({
  path: '$collectId/auditoria/$version',
  getParentRoute: () => CollectsRoute,
  component: withAuthenticationRequired(AuditView),

  loader: async ({
    params: { initiativeId, featureId, collectId, version },
  }) => {
    const results = await Promise.allSettled([
      queryClient.ensureQueryData(
        getCollectQueryOptions(
          initiativeId,
          featureId,
          'web',
          collectId,
          version
        )
      ),
      queryClient.ensureQueryData(
        getCollectQueryOptions(
          initiativeId,
          featureId,
          'mobile',
          collectId,
          version
        )
      ),
    ])
    if (results.every((result) => result.status === 'rejected')) {
      throw notFound()
    }
  },
})

export const NotAuthorizedRoute = createRoute({
  path: '/acesso-negado',
  getParentRoute: () => LocaleRoute,
  component: NotAuthorized,
  staticData: { noLayout: true },
  validateSearch: (
    search: Record<string, unknown>
  ): NotAuthorizedSearchSchema => parseNotAuthorizedSearch(search),
})

export const TermsOfUseRoute = createRoute({
  path: 'termos-de-uso',
  getParentRoute: () => LocaleRoute,
  component: TermsOfUse,
  staticData: { noLayout: true },
})

export const FeatureConstructorForm = createRoute({
  path: '$featureId/construtor',
  getParentRoute: () => FeaturesRoute,
  component: withAuthenticationRequired(ConstructorFieldsLayout),
  staticData: { noLayout: true },
  beforeLoad: ({ params: { featureId, initiativeId, locale } }) => {
    const user = getCurrentUser()
    if (
      !user?.hasPermission(
        PermissionLevel.ADMIN,
        Number(initiativeId),
        Number(featureId)
      )
    ) {
      redirectToNotAuthorized({ initiativeId, locale })
    }
  },
})

export const ImportedDataRoute = createRoute({
  path: '$initiativeId/importar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(ImportDataView),
  loader: ensureInitiative,
  beforeLoad: requireInitiativeAdmin,
})

export const ExportDataRoute = createRoute({
  path: '$initiativeId/exportar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(ExportDataView),
  loader: ensureInitiative,
  beforeLoad: requireInitiativeAdmin,
})

export const InitiativeImportRoute = createRoute({
  path: '$initiativeId/importacoes',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeImports),
  loader: ensureInitiative,
  beforeLoad: requireInitiativeAdmin,
})

export const InitiativeExportRoute = createRoute({
  path: '$initiativeId/exportacoes',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeExports),
  loader: ensureInitiative,
  beforeLoad: requireInitiativeAdmin,
})

export const MissingLocaleRedirectRoute = createRoute({
  path: '$',
  getParentRoute: () => Route,
  beforeLoad: ({ params: { _splat }, location }) => {
    const firstSegment = _splat?.split('/')[0]
    if (!_splat || (firstSegment && isLanguageSupported(firstSegment))) {
      throw notFound()
    }
    throw redirect({
      href: `/${resolveLocale(navigator.language)}${location.href}`,
      replace: true,
    })
  },
})

export const SelfAppliedCollectRedirectRoute = createRoute({
  path: 'collect/$key',
  getParentRoute: () => Route,
  beforeLoad: () => {
    redirectTo(SelfAppliedCollectRoute.to)
  },
})

export const SelfAppliedCollectFinishedRedirectRoute = createRoute({
  path: 'collect/$key/finished',
  getParentRoute: () => Route,
  beforeLoad: () => {
    redirectTo(SelfAppliedCollectFinishedRoute.to)
  },
})

export const SelfAppliedCollectTotpRedirectRoute = createRoute({
  path: 'collect/$key/private',
  getParentRoute: () => Route,
  beforeLoad: () => {
    redirectTo(SelfAppliedCollectTotpRoute.to)
  },
})

export const SelfAppliedCollectRoute = createRoute({
  path: 'collect/$key',
  getParentRoute: () => LocaleRoute,
  component: SelfAppliedCollect,
  staticData: { noLayout: true },
})

export const SelfAppliedCollectFinishedRoute = createRoute({
  path: 'collect/$key/finished',
  getParentRoute: () => LocaleRoute,
  component: SelfAppliedCollectFinished,
  staticData: { noLayout: true },
})

export const SelfAppliedCollectTotpRoute = createRoute({
  path: 'collect/$key/private',
  getParentRoute: () => LocaleRoute,
  component: SelfAppliedCollectTotp,
  staticData: { noLayout: true },
  validateSearch: (
    search: Record<string, unknown>
  ): SelfAppliedCollectTotpSearchSchema => {
    const otp =
      typeof search.otp === 'string' || typeof search.otp === 'number'
        ? String(search.otp)
        : ''
    return { otp: /^\d{6}$/.test(otp) ? otp : undefined }
  },
})

export const InitiativesDuplicateRoute = createRoute({
  path: '$initiativeId/duplicar',
  getParentRoute: () => InitiativesRoute,
  component: withAuthenticationRequired(InitiativeDuplicate),
  loader: async ({ params: { initiativeId } }) => {
    try {
      await queryClient.ensureQueryData(getInitiativeQueryOptions(initiativeId))
    } catch {
      throw notFound()
    }
  },
})
